<script>
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength, email } from '@vuelidate/validators';
import { isPhoneNumberValid } from 'shared/helpers/Validators';
import parsePhoneNumber from 'libphonenumber-js';
export default {
  components: {
    NextButton,
  },
  props: {
    name: {
      type: String,
      default: '',
    },
    email: {
      type: String,
      default: '',
    },
    displayName: {
      type: String,
      default: '',
    },
    emailEnabled: {
      type: Boolean,
      default: false,
    },
    phoneNumber: {
      type: String,
      default: '',
    },
  },
  emits: ['updateUser'],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      userName: this.name,
      userDisplayName: this.displayName,
      userEmail: this.email,
      userPhoneNumber: this.phoneNumber,
      activeDialCode: '',
      inputStyles: {
        borderRadius: '0.75rem',
        padding: '0.375rem 0.75rem',
        fontSize: '0.875rem',
        marginBottom: '0.125rem',
      },
    };
  },
  validations: {
    userName: {
      required,
      minLength: minLength(1),
    },
    userDisplayName: {},
    userEmail: {
      required,
      email,
    },
    userPhoneNumber: {},
  },
  computed: {
    parsePhoneNumber() {
      return parsePhoneNumber(this.userPhoneNumber);
    },
    isPhoneNumberNotValid() {
      if (this.userPhoneNumber !== '') {
        return (
          !isPhoneNumberValid(this.userPhoneNumber, this.activeDialCode) ||
          (this.userPhoneNumber !== '' ? this.activeDialCode === '' : false)
        );
      }
      return false;
    },
    phoneNumberError() {
      if (this.activeDialCode === '') {
        return this.$t('PROFILE_SETTINGS.FORM.PHONE_NUMBER.DIAL_CODE_ERROR');
      }
      if (!isPhoneNumberValid(this.userPhoneNumber, this.activeDialCode)) {
        return this.$t('PROFILE_SETTINGS.FORM.PHONE_NUMBER.ERROR');
      }
      return '';
    },
    setPhoneNumber() {
      if (this.parsePhoneNumber && this.parsePhoneNumber.countryCallingCode) {
        return this.userPhoneNumber;
      }
      if (this.userPhoneNumber === '' && this.activeDialCode !== '') {
        return '';
      }
      return this.activeDialCode
        ? `${this.activeDialCode}${this.userPhoneNumber}`
        : '';
    },
  },
  watch: {
    name: {
      handler(value) {
        this.userName = value;
      },
      immediate: true,
    },
    displayName: {
      handler(value) {
        this.userDisplayName = value;
      },
      immediate: true,
    },
    email: {
      handler(value) {
        this.userEmail = value;
      },
      immediate: true,
    },
    phoneNumber: {
      handler(value) {
        this.userPhoneNumber = value;
        this.setDialCode();
      },
      immediate: true,
    },
  },
  methods: {
    setDialCode() {
      if (
        this.userPhoneNumber !== '' &&
        this.parsePhoneNumber &&
        this.parsePhoneNumber.countryCallingCode
      ) {
        const dialCode = this.parsePhoneNumber.countryCallingCode;
        this.activeDialCode = `+${dialCode}`;
      }
    },
    setPhoneCode(code) {
      if (this.userPhoneNumber !== '' && this.parsePhoneNumber) {
        const dialCode = this.parsePhoneNumber.countryCallingCode;
        if (dialCode === code) {
          return;
        }
        this.activeDialCode = `+${dialCode}`;
        const newPhoneNumber = this.userPhoneNumber.replace(
          `+${dialCode}`,
          `${code}`
        );
        this.userPhoneNumber = newPhoneNumber;
      } else {
        this.activeDialCode = code;
      }
    },
    async updateUser() {
      this.v$.$touch();
      if (this.v$.$invalid || this.isPhoneNumberNotValid) {
        useAlert(this.$t('PROFILE_SETTINGS.FORM.ERROR'));
        return;
      }
      this.$emit('updateUser', {
        name: this.userName,
        displayName: this.userDisplayName,
        email: this.userEmail,
        phoneNumber: this.setPhoneNumber,
      });
    },
  },
};
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="updateUser('profile')">
    <woot-input
      v-model="userName"
      :styles="inputStyles"
      :class="{ error: v$.userName.$error }"
      :label="$t('PROFILE_SETTINGS.FORM.NAME.LABEL')"
      :placeholder="$t('PROFILE_SETTINGS.FORM.NAME.PLACEHOLDER')"
      :error="`${
        v$.userName.$error ? $t('PROFILE_SETTINGS.FORM.NAME.ERROR') : ''
      }`"
      @input="v$.userName.$touch"
      @blur="v$.userName.$touch"
    />
    <!-- <woot-input
      v-model="userDisplayName"
      :styles="inputStyles"
      :class="{ error: v$.userDisplayName.$error }"
      :label="$t('PROFILE_SETTINGS.FORM.DISPLAY_NAME.LABEL')"
      :placeholder="$t('PROFILE_SETTINGS.FORM.DISPLAY_NAME.PLACEHOLDER')"
      :error="`${
        v$.userDisplayName.$error
          ? $t('PROFILE_SETTINGS.FORM.DISPLAY_NAME.ERROR')
          : ''
      }`"
      @input="v$.userDisplayName.$touch"
      @blur="v$.userDisplayName.$touch"
    /> -->
    <woot-input
      v-if="emailEnabled"
      v-model="userEmail"
      :styles="inputStyles"
      :class="{ error: v$.userEmail.$error }"
      :label="$t('PROFILE_SETTINGS.FORM.EMAIL.LABEL')"
      :placeholder="$t('PROFILE_SETTINGS.FORM.EMAIL.PLACEHOLDER')"
      :error="`${
        v$.userEmail.$error ? $t('PROFILE_SETTINGS.FORM.EMAIL.ERROR') : ''
      }`"
      @input="v$.userEmail.$touch"
      @blur="v$.userEmail.$touch"
    />
    <div class="flex flex-col gap-1">
      <label
        :class="{
          error: isPhoneNumberNotValid,
        }"
        class="text-sm font-medium text-slate-800 dark:text-slate-100"
      >
        {{ $t('PROFILE_SETTINGS.FORM.PHONE_NUMBER.LABEL') }}
      </label>
      <woot-phone-input
        v-model="userPhoneNumber"
        class=""
        :styles="inputStyles"
        :value="userPhoneNumber"
        :error="isPhoneNumberNotValid"
        :placeholder="$t('PROFILE_SETTINGS.FORM.PHONE_NUMBER.PLACEHOLDER')"
        @blur="v$.userPhoneNumber.$touch"
        @set-code="setPhoneCode"
      />
      <span v-if="isPhoneNumberNotValid" class="text-xs text-red-400">
        {{ phoneNumberError }}
      </span>
    </div>
    <div>
      <NextButton type="submit" :label="$t('PROFILE_SETTINGS.BTN_TEXT')" />
    </div>
  </form>
</template>

<style scoped>
::v-deep .phone-input--wrap > div:first-child {
  /* Apply rounded corners like regular inputs */
  border-radius: 12px;
  /* Reduce the bottom margin to match other inputs */
  margin-bottom: 2px;
}

::v-deep .phone-input--wrap .cursor-pointer {
  /* Style the country selector to match the rounded corners */
  border-top-left-radius: 12px;
  border-bottom-left-radius: 12px;
}

::v-deep .phone-input--wrap input {
  /* Style the phone number input to match regular inputs */
  border-top-right-radius: 12px;
  border-bottom-right-radius: 12px;
  padding: 6px 12px;
  font-size: 14px;
}
</style>
